/* istanbul ignore file */
import { useToken } from '@ori/auth';
import { useServiceUrls } from '@ori/configuration-manager';
import { useEnvironmentConfiguration } from '@ori/environment';
import { TranslationsNamespaceProvider, TranslationsProvider, useTranslationsWithNamespaces } from '@ori/i18n';
import { ErrorBoundary } from '@ori/presentation-components';
import { ApiClientProvider as StaticApiClientProvider } from '@ori/static-api-client-react';
import { Team, TestIdProvider } from '@ori/testid-generator';
import type { FC } from 'react';
import React, { Suspense } from 'react';

import { NotifyPopup } from '../../components';
import { PACKAGE_NAME, defaultTranslations } from '../../constants';
import { AppContextProvider } from '../../contexts';
import type { BackInStockNotificationControllerProps } from './types';

export const BackInStockNotificationController: FC<BackInStockNotificationControllerProps> = ({ translations }) => {
  const { tenant = '' } = useEnvironmentConfiguration();
  const { language } = useTranslationsWithNamespaces();
  const { staticApiUrl } = useServiceUrls(['staticApi']);
  const { token } = useToken();

  return (
    <TestIdProvider
      team={Team.Presentation}
      project={PACKAGE_NAME}
    >
      <ErrorBoundary>
        <Suspense fallback={null}>
          <TranslationsProvider
            language={language}
            defaultTranslationsWithNamespaces={{ [PACKAGE_NAME]: defaultTranslations }}
            translationsWithNamespaces={translations ? { [PACKAGE_NAME]: translations } : undefined}
            // eslint-disable-next-line no-console -- we need to log errors
            clientFetchConfiguration={{ tenant, token, baseUrl: staticApiUrl, onError: console.error }}
          >
            <TranslationsNamespaceProvider namespace={PACKAGE_NAME}>
              <StaticApiClientProvider
                baseUrl={staticApiUrl}
                language={language}
                tenant={tenant}
                token={token}
              >
                <AppContextProvider>
                  <NotifyPopup />
                </AppContextProvider>
              </StaticApiClientProvider>
            </TranslationsNamespaceProvider>
          </TranslationsProvider>
        </Suspense>
      </ErrorBoundary>
    </TestIdProvider>
  );
};
